import React from "react";
import { Link } from "react-router-dom";
import { Clock, Users, Bookmark, BookmarkCheck, Utensils } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const seasonHues = {
  spring: "text-lime-400",
  summer: "text-amber-400",
  autumn: "text-orange-500",
  winter: "text-sky-300",
};

const seasonPatterns = {
  spring: "from-lime-950 via-stone-900 to-stone-950",
  summer: "from-amber-950 via-stone-900 to-stone-950",
  autumn: "from-orange-950 via-stone-900 to-stone-950",
  winter: "from-sky-950 via-stone-900 to-stone-950",
};

const RecipeCard = ({ recipe, index = 0, showFavorite = true }) => {
  const { user, favoriteIds, toggleFavorite } = useAuth();
  const isFav = favoriteIds.has(recipe.id);

  const heroImg = recipe.image_data
    ? `data:image/png;base64,${recipe.image_data}`
    : recipe.image_status === "ready"
    ? `${BACKEND}/api/recipes/${recipe.id}/og_image`
    : null;

  const handleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Sign in to save recipes");
      return;
    }
    try {
      const nowFav = await toggleFavorite(recipe.id);
      toast.success(nowFav ? "Saved to cookbook" : "Removed from cookbook");
    } catch (err) {
      toast.error("Could not update favorite");
    }
  };

  return (
    <Link
      to={`/recipe/${recipe.id}`}
      data-testid={`recipe-card-${recipe.id}`}
      className="group block border border-stone-800 bg-stone-900/40 hover:border-orange-700 transition-colors"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-stone-900">
        {heroImg ? (
          <img
            src={heroImg}
            alt={recipe.title}
            className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${seasonPatterns[recipe.season] || "from-stone-900 to-stone-950"} flex flex-col items-center justify-center gap-4 p-6`}>
            <Utensils className="w-8 h-8 text-orange-600/50" strokeWidth={1.25} />
            <p className="font-serif-display italic text-sm text-stone-500 text-center">
              Photograph on its way
            </p>
          </div>
        )}
        <div className="absolute top-4 left-4 flex items-center gap-3">
          <span className="bg-stone-950/80 backdrop-blur-md px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-stone-200">
            {recipe.course}
          </span>
          <span className={`text-[10px] uppercase tracking-[0.24em] ${seasonHues[recipe.season] || "text-stone-300"}`}>
            {recipe.season}
          </span>
          {recipe.is_vegan && (
            <span
              data-testid={`vegan-badge-${recipe.id}`}
              className="bg-lime-500 text-stone-950 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-medium"
            >
              Vegan
            </span>
          )}
        </div>
        {showFavorite && (
          <button
            data-testid={`favorite-toggle-${recipe.id}`}
            onClick={handleFav}
            className="absolute top-4 right-4 w-9 h-9 bg-stone-950/80 backdrop-blur-md flex items-center justify-center text-stone-200 hover:text-orange-500 transition-colors"
            aria-label={isFav ? "Remove from cookbook" : "Save to cookbook"}
          >
            {isFav ? (
              <BookmarkCheck className="w-4 h-4 text-orange-500" />
            ) : (
              <Bookmark className="w-4 h-4" />
            )}
          </button>
        )}
      </div>

      <div className="p-6 space-y-4">
        <div>
          <div className="eyebrow mb-2">{recipe.region}</div>
          <h3 className="font-serif-display text-2xl leading-tight text-stone-100 group-hover:text-orange-500 transition-colors">
            {recipe.title}
          </h3>
          {recipe.subtitle && (
            <p className="text-sm text-stone-400 italic mt-2 line-clamp-2">{recipe.subtitle}</p>
          )}
        </div>
        <div className="flex items-center gap-5 text-xs text-stone-500 font-mono-accent">
          <span className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            {recipe.prep_time_min + recipe.cook_time_min}m
          </span>
          <span className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" />
            {recipe.servings}
          </span>
          <span className="ml-auto uppercase tracking-widest">{recipe.difficulty}</span>
        </div>
      </div>
    </Link>
  );
};

export default RecipeCard;
